import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  Bot,
  CheckCircle2,
  ChevronRight,
  Database,
  FileInput,
  Home,
  KeyRound,
  LayoutTemplate,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Search,
  ShieldCheck,
  Sun,
  Tv,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { adminApi } from "../api/admin";
import { adminApprovalsApi } from "../api/collab";
import { NotificationsBell } from "../components/NotificationsBell";
import { useAuth } from "../auth/AuthContext";
import { Avatar, Button, Menu } from "../ds";
import { applyTheme, getStoredTheme, type Theme } from "../theme";
import { CommandPalette } from "./CommandPalette";
import { basePath, modeFor, type Mode } from "./role";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
}

function navFor(mode: Mode, pendingDevices: number, pendingApprovals = 0): Array<{ label: string; items: NavItem[] }> {
  const b = basePath(mode);
  if (mode === "viewer") {
    return [{ label: "Workspace", items: [{ to: "/viewer/reports", label: "Reports", icon: BarChart3 }] }];
  }
  if (mode === "owner") {
    return [
      { label: "Workspace", items: [{ to: "/owner/reports", label: "My reports", icon: BarChart3 }, { to: "/templates", label: "Templates", icon: LayoutTemplate }] },
      { label: "Build with AI", items: [{ to: "/settings/api-tokens", label: "AI & API access", icon: Bot }] },
    ];
  }
  return [
    {
      label: "Workspace",
      items: [
        { to: b, label: "Home", icon: Home, end: true },
        { to: `${b}/reports`, label: "Reports", icon: BarChart3 },
      ],
    },
    {
      label: "Data",
      items: [{ to: `${b}/data`, label: "Data", icon: Database }],
    },
    {
      label: "TV screens",
      items: [
        { to: `${b}/approvals`, label: "Approvals", icon: CheckCircle2, badge: pendingApprovals || undefined },
        { to: `${b}/devices`, label: "Devices", icon: Tv, badge: pendingDevices || undefined },
        { to: `${b}/device-groups`, label: "Screen groups", icon: Tv },
      ],
    },
    {
      label: "Build faster",
      items: [
        { to: "/templates", label: "Templates", icon: LayoutTemplate },
        { to: `${b}/import/powerbi`, label: "Import from Power BI", icon: FileInput },
        { to: "/settings/api-tokens", label: "AI & API access", icon: Bot },
      ],
    },
    {
      label: "Administration",
      items: [
        { to: `${b}/users`, label: "People & roles", icon: Users },
        { to: `${b}/tenants`, label: "Organizations", icon: ShieldCheck },
        { to: `${b}/ai-activity`, label: "AI activity", icon: Bot },
        { to: `${b}/audit`, label: "Audit log", icon: ScrollText },
        { to: `${b}/system-health`, label: "System health", icon: Activity },
      ],
    },
  ];
}

const CRUMB_LABELS: Record<string, string> = {
  admin: "Home",
  owner: "My reports",
  viewer: "Reports",
  reports: "Reports",
  "data-sources": "Data sources",
  datasets: "Datasets",
  devices: "Devices",
  "device-groups": "Screen groups",
  users: "People & roles",
  tenants: "Organizations",
  audit: "Audit log",
  "ai-activity": "AI activity",
  approvals: "Approvals",
  "system-health": "System health",
  import: "Import",
  powerbi: "Power BI",
  settings: "Settings",
  "api-tokens": "AI & API access",
  templates: "Templates",
};

let crumbTitle: string | null = null;
const crumbListeners = new Set<(t: string | null) => void>();
function setCrumbTitle(t: string | null) {
  crumbTitle = t;
  crumbListeners.forEach((l) => l(t));
}

/** Lets a detail page put a human name (e.g. the report name) in the breadcrumb instead of an id. */
export function usePageTitle(title: string | null | undefined) {
  useEffect(() => {
    setCrumbTitle(title ?? null);
    if (title) document.title = `${title} · Maridive Reports`;
    return () => {
      setCrumbTitle(null);
      document.title = "Maridive Reports Platform";
    };
  }, [title]);
}

function Breadcrumbs() {
  const loc = useLocation();
  const [title, setTitle] = useState<string | null>(crumbTitle);
  useEffect(() => {
    crumbListeners.add(setTitle);
    return () => {
      crumbListeners.delete(setTitle);
    };
  }, []);
  const parts = loc.pathname.split("/").filter(Boolean);
  const crumbs: Array<{ to: string; label: string }> = [];
  let acc = "";
  parts.forEach((p, i) => {
    acc += `/${p}`;
    const known = CRUMB_LABELS[p];
    const isId = /^[0-9a-f-]{16,}$/i.test(p);
    if (i === 0 && parts.length > 1 && (p === "admin" || p === "owner" || p === "viewer")) {
      if (p === "admin") crumbs.push({ to: acc, label: "Home" });
      return;
    }
    if (isId) crumbs.push({ to: acc, label: title ?? "Details" });
    else if (known) crumbs.push({ to: acc, label: known });
  });
  return (
    <nav className="ds-crumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <span key={c.to} className="ds-row" style={{ gap: 6, minWidth: 0 }}>
          {i > 0 ? <ChevronRight size={14} className="ds-faint" /> : null}
          {i === crumbs.length - 1 ? <strong className="ds-truncate">{c.label}</strong> : <Link to={c.to}>{c.label}</Link>}
        </span>
      ))}
    </nav>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { user, logout } = useAuth();
  const mode = modeFor(user);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => window.localStorage.getItem("mrp-nav-collapsed") === "1");
  const [theme, setTheme] = useState<Theme>(getStoredTheme());
  const [paletteOpen, setPaletteOpen] = useState(false);

  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices, enabled: mode === "admin", staleTime: 60_000 });
  const pending = useMemo(() => (devices.data ?? []).filter((d) => d.status === "pending").length, [devices.data]);
  const approvals = useQuery({ queryKey: ["approvals-count"], queryFn: adminApprovalsApi.count, enabled: mode === "admin", refetchInterval: 60_000, staleTime: 30_000, retry: false });
  const groups = navFor(mode, pending, approvals.data?.total ?? 0);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const toggleTheme = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    setTheme(next);
  };
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      window.localStorage.setItem("mrp-nav-collapsed", v ? "0" : "1");
      return !v;
    });
  };

  return (
    <div className={`ds-shell${collapsed ? " is-collapsed" : ""}`}>
      <aside className="ds-sidebar">
        <Link to={basePath(mode) + (mode === "admin" ? "" : "/reports")} className="ds-sidebar__brand" style={{ textDecoration: "none" }}>
          <span className="ds-sidebar__brand-mark">
            <BarChart3 size={18} />
          </span>
          {!collapsed ? (
            <span>
              Maridive Reports
              <small>Dashboards for every screen</small>
            </span>
          ) : null}
        </Link>
        <nav aria-label="Main">
          {groups.map((g) => (
            <div key={g.label} className="ds-nav-group">
              <div className="ds-nav-group__label">{g.label}</div>
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className="ds-nav-link" title={collapsed ? it.label : undefined}>
                  <it.icon size={18} />
                  {!collapsed ? <span className="ds-truncate">{it.label}</span> : null}
                  {!collapsed && it.badge ? <span className="ds-badge">{it.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="ds-sidebar__footer">
          <Button variant="ghost" icon={collapsed ? PanelLeftOpen : PanelLeftClose} onClick={toggleCollapsed} title={collapsed ? "Expand menu" : "Collapse menu"}>
            {!collapsed ? "Collapse" : null}
          </Button>
        </div>
      </aside>
      <div className="ds-main">
        <header className="ds-topbar">
          <Breadcrumbs />
          <div className="ds-grow" />
          <button className="ds-search-trigger" onClick={() => setPaletteOpen(true)} aria-label="Search reports and pages">
            <Search size={15} />
            Search reports, pages, actions…
            <span className="ds-kbd">Ctrl K</span>
          </button>
          <NotificationsBell isAdmin={mode === "admin"} />
          <Button variant="ghost" icon={theme === "dark" ? Sun : Moon} onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} title={theme === "dark" ? "Light mode" : "Dark mode"} />
          <Menu
            trigger={(p) => (
              <button {...p} className="ds-btn ds-btn--ghost" style={{ gap: 10, padding: "0 8px" }} aria-label="Account menu">
                <Avatar name={user?.display_name ?? user?.email} />
                <span className="ds-truncate" style={{ maxWidth: 160 }}>{user?.display_name ?? user?.email}</span>
              </button>
            )}
            items={[
              { label: "AI & API access", icon: KeyRound, onSelect: () => navigate("/settings/api-tokens") },
              { label: theme === "dark" ? "Light mode" : "Dark mode", icon: theme === "dark" ? Sun : Moon, onSelect: toggleTheme },
              { label: "", divider: true, onSelect: () => undefined },
              { label: "Sign out", icon: LogOut, danger: true, onSelect: () => void logout() },
            ]}
          />
        </header>
        <main className="ds-content">{children ?? <Outlet />}</main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} mode={mode} navGroups={groups} />
    </div>
  );
}
